// <SurveyWizard> — composant générique multi-étapes piloté par un schéma JSON.
//
// Un seul composant pour les deux questionnaires (coach / sportive) : il consomme
// le schéma servi par le backend (GET /api/surveys/schema/{form}), gère le
// branchement conditionnel (visible_if), la barre de progression par section,
// la sauvegarde locale (localStorage, clé = UUID anonyme), la validation par
// étape, puis POST la réponse et purge le stockage local.
//
// Props :
//   form   : 'coach' | 'athlete'
//   accent : couleur primaire CSS (optionnel, défaut violet CycleGala)
//
// Dépendances : React uniquement (+ surveyApi.js, visibleIf.js). Mobile-first.
import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchCohortPhase, fetchSurveySchema, registerSurveyCode, resolveSurveySession, submitSurvey,
} from './surveyApi';
import { evalCondition } from './visibleIf';
import './SurveyWizard.css';

// ── Helpers de logique (visibilité, "Non applicable", validation) ────────────

const isVisible = (q, a) => evalCondition(q.visible_if, a);

const naForced = (q, a) => !!(q.na && q.na.forcedWhen && evalCondition(q.na.forcedWhen, a));

const naAvailable = (q, a) => {
  if (!q.na) return false;
  if (q.na.forcedWhen && evalCondition(q.na.forcedWhen, a)) return true; // forcé => disponible
  if (q.na.availableWhen) return evalCondition(q.na.availableWhen, a);   // conditionné
  return true;                                                          // sinon toujours disponible
};

// « Non applicable » n'est retenu que s'il est disponible (évite un état résiduel
// après bascule de branche has_coach).
const naSelected = (q, a) => naAvailable(q, a) && (naForced(q, a) || a[`__na_${q.id}`] === true);

function isAnswered(q, a) {
  if (!isVisible(q, a)) return true;
  switch (q.type) {
    case 'boolean_branch': return typeof a[q.id] === 'boolean';
    case 'single_select':  return a[q.id] != null && a[q.id] !== '';
    case 'multi_select':   return Array.isArray(a[q.id]) && a[q.id].length > 0;
    case 'scale_1_5':      return naSelected(q, a) || Number.isInteger(a[q.id]);
    case 'short_text':     return !!(a[q.id] && a[q.id].trim());
    default:               return true;
  }
}

function sectionErrors(section, a) {
  const errs = {};
  for (const q of section.questions) {
    if (!isVisible(q, a)) continue;
    if (q.required && !isAnswered(q, a)) {
      errs[q.id] = 'Merci de répondre à cette question.';
    }
    if (q.allowOther && q.otherField) {
      const otherOn =
        (q.type === 'single_select' && a[q.id] === 'autre') ||
        (q.type === 'multi_select' && a[`__other_${q.id}`] === true);
      if (otherOn && !(a[q.otherField] || '').trim()) {
        errs[q.otherField] = 'Précise ta réponse.';
      }
    }
  }
  return errs;
}

// Construit le corps JSON envoyé au backend à partir de l'état des réponses.
// Les champs non visibles sont neutralisés (null / [] / champ NA à true).
// `schema.sections` ne contient que les sections de la phase en cours : le payload
// est donc automatiquement restreint à la vague, sans filtrage supplémentaire ici.
function buildPayload(schema, a, participantCode) {
  const payload = { participant_code: participantCode };
  for (const section of schema.sections) {
    for (const q of section.questions) {
      const visible = isVisible(q, a);
      if (q.type === 'multi_select') {
        payload[q.id] = visible && Array.isArray(a[q.id]) ? a[q.id] : [];
        if (q.otherField) {
          const on = visible && a[`__other_${q.id}`] === true;
          payload[q.otherField] = on ? (a[q.otherField] || null) : null;
        }
      } else if (q.type === 'short_text') {
        payload[q.id] = visible ? (a[q.id] || null) : null;
      } else if (q.type === 'single_select' || q.type === 'boolean_branch') {
        payload[q.id] = visible ? (a[q.id] ?? null) : null;
        if (q.otherField) {
          const on = visible && a[q.id] === 'autre';
          payload[q.otherField] = on ? (a[q.otherField] || null) : null;
        }
      } else if (q.type === 'scale_1_5') {
        const na = naSelected(q, a);
        payload[q.id] = visible && !na ? (a[q.id] ?? null) : null;
        if (q.na && q.na.field) payload[q.na.field] = na;
      }
    }
  }
  return payload;
}

// ── Code participant & persistance locale ────────────────────────────────────

// Pseudonyme de la répondante. Deux provenances :
//  - le lien d'invitation personnel (?code=SPORTIVE_48213) ;
//  - la saisie : elle le crée depuis le lien de son groupe (?groupe=g2), puis le
//    retape à chaque étape. Personne dans l'équipe ne sait alors qui est qui.
// Jamais un nom ni un e-mail. Même motif que côté serveur (ParticipantCode).
const CODE_PATTERN = /^[A-Za-z0-9_-]{3,32}$/;
// Code choisi par la participante : 6 caractères au moins (SelfChosenCode).
const NEW_CODE_PATTERN = /^[A-Za-z0-9_-]{6,32}$/;
const GROUP_PATTERN = /^[a-z0-9_-]{1,32}$/;

const CODE_KEY = 'cyclegala_survey_participant_code';

function readParam(name, pattern) {
  try {
    const v = new URLSearchParams(window.location.search).get(name);
    return v && pattern.test(v) ? v : null;
  } catch (_) { return null; }
}

function loadParticipantCode() {
  const fromUrl = readParam('code', CODE_PATTERN);
  if (fromUrl) {
    rememberCode(fromUrl);
    return fromUrl;
  }
  try { return localStorage.getItem(CODE_KEY) || ''; } catch (_) { return ''; }
}

function rememberCode(code) {
  try {
    if (code) localStorage.setItem(CODE_KEY, code);
    else localStorage.removeItem(CODE_KEY);
  } catch (_) { /* ignore */ }
}

// ── Saisie / création du code ────────────────────────────────────────────────

// Deux gestes distincts, et c'est voulu : « je crée » refuse un code déjà pris
// (sinon on entrerait dans le dossier d'une autre), « j'ai déjà un code » refuse un
// code inconnu. La création n'est proposée que depuis un lien de groupe (c'est lui
// qui dit au serveur dans quel calendrier l'inscrire) ET pendant la phase amont de
// ce groupe (`canCreate`) : après, le même lien ne sert plus qu'à se reconnecter.
function CodeEntry({ group, canCreate, notice, onReady, style }) {
  const [mode, setMode] = useState(canCreate ? 'create' : 'login');
  const [code, setCode] = useState('');
  const [error, setError] = useState(notice || null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const creating = mode === 'create';

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!(creating ? NEW_CODE_PATTERN : CODE_PATTERN).test(code)) {
      setError(creating
        ? '6 caractères minimum : lettres, chiffres, tiret ou tiret bas, sans espace.'
        : 'Ce code ne peut pas être valide : vérifie la saisie.');
      return;
    }
    setBusy(true);
    try {
      if (creating) {
        await registerSurveyCode(code, group);
        setCreated(code);
      } else {
        // Tel que tapé d'abord : des codes anciens mêlent majuscules et minuscules
        // (« Sportive0416 »). Puis en majuscules, pour qui a créé « LUNE42 » et le
        // retape en minuscules.
        for (const candidate of [...new Set([code, code.toUpperCase()])]) {
          // eslint-disable-next-line no-await-in-loop
          const s = await resolveSurveySession(candidate);
          if (s.registered) { onReady(candidate); return; }
        }
        setError('Code inconnu. Vérifie la saisie : majuscules, minuscules et tirets comptent.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Une fois créé, on s'arrête un instant : ce code ne pourra être redonné par
  // personne, autant qu'elle le note avant de commencer.
  if (created) {
    return (
      <div className="sv-root" style={style}>
        <div className="sv-card sv-thanks">
          <div className="sv-thanks-emoji">🔑</div>
          <h2>Ton code est créé</h2>
          <p className="sv-code-big">{created}</p>
          <p>
            Note-le maintenant (capture d'écran, notes du téléphone…). Il te sera
            demandé à chaque étape de l'étude, et personne ne pourra te le redonner :
            nous ne savons pas quel code t'appartient.
          </p>
          <button type="button" className="sv-next" onClick={() => onReady(created)}>
            J'ai noté mon code, commencer ›
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sv-root" style={style}>
      <div className="sv-header">
        <h1 className="sv-title">Ton code de participation</h1>
        <p className="sv-intro">
          Tes réponses sont enregistrées sous ce code, jamais sous ton nom.
        </p>
      </div>
      <form className="sv-form" onSubmit={submit}>
        {canCreate && (
          <div className="sv-branch sv-code-modes">
            <button
              type="button" className={`sv-branch-btn ${creating ? 'is-active' : ''}`}
              aria-pressed={creating}
              onClick={() => { setMode('create'); setError(null); setCode((c) => c.toUpperCase()); }}
            >
              C'est ma première fois : je crée mon code
            </button>
            <button
              type="button" className={`sv-branch-btn ${!creating ? 'is-active' : ''}`}
              aria-pressed={!creating} onClick={() => { setMode('login'); setError(null); }}
            >
              J'ai déjà un code
            </button>
          </div>
        )}
        <div className="sv-card">
          <label className="sv-label" htmlFor="sv-code">
            {creating ? 'Choisis ton code' : 'Ton code'}
          </label>
          <input
            id="sv-code" className="sv-text sv-code-input" type="text"
            autoComplete="off" autoCapitalize={creating ? 'characters' : 'none'}
            spellCheck={false} maxLength={32} value={code}
            // À la création, majuscules imposées : « lune42 » et « LUNE42 » seraient
            // deux codes, donc deux personnes. À la reconnexion, on garde la saisie
            // telle quelle — un code existant peut contenir des minuscules.
            onChange={(e) => setCode(creating
              ? e.target.value.toUpperCase().trim()
              : e.target.value.trim())}
          />
          {creating && (
            <p className="sv-code-hint">
              6 caractères minimum, lettres et chiffres (ex. LUNE42). <strong>N'utilise
              ni ton prénom, ni ton nom, ni ta date de naissance</strong> : ton code doit
              rester anonyme.
            </p>
          )}
          {!canCreate && (
            <p className="sv-code-hint">
              {group
                ? "Les inscriptions à l'étude sont closes : seules les participantes déjà inscrites continuent, avec leur code."
                : "Pas encore de code ? Ouvre le lien d'inscription envoyé à ton groupe."}
            </p>
          )}
          {error && <div className="sv-field-error">{error}</div>}
        </div>
        <div className="sv-actions">
          <span />
          <button type="submit" className="sv-next" disabled={busy || !code}>
            {busy ? 'Vérification…' : creating ? 'Créer mon code' : 'Continuer ›'}
          </button>
        </div>
      </form>
    </div>
  );
}

// La clé de brouillon inclut la vague ET le groupe : l'ouverture d'une nouvelle
// vague ne doit pas reprendre les réponses à demi remplies de la précédente, et
// depuis que les groupes ont des calendriers décalés, la phase seule n'est plus
// unique dans l'étude (un même navigateur peut légitimement voir deux groupes).
const draftKey = (form, cohort, phase) => `cyclegala_survey_${form}_${cohort || 'na'}_${phase}`;

function loadState(form, cohort, phase) {
  try {
    const raw = localStorage.getItem(draftKey(form, cohort, phase));
    if (raw) return JSON.parse(raw);
  } catch (_) { /* stockage indisponible */ }
  return { section: 0, answers: {} };
}

// ── Rendu des types de question ──────────────────────────────────────────────

function Scale({ q, value, na, onPick, onNa, showNa, disabledNa }) {
  const sc = q.scale || {};
  const minLabel = sc.minLabel || '1';
  const maxLabel = sc.maxLabel || '5';
  return (
    <div className="sv-scale">
      <div className="sv-scale-btns" role="radiogroup" aria-label={q.label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n} type="button"
            className={`sv-scale-btn ${value === n && !na ? 'is-active' : ''}`}
            aria-pressed={value === n && !na}
            onClick={() => onPick(n)}
          >{n}</button>
        ))}
      </div>
      <div className="sv-scale-ends"><span>{minLabel}</span><span>{maxLabel}</span></div>
      {showNa && (
        <button
          type="button"
          className={`sv-na ${na ? 'is-active' : ''}`}
          aria-pressed={na}
          disabled={disabledNa}
          onClick={onNa}
        >{q.na.label}</button>
      )}
    </div>
  );
}

function Question({ q, answers, setAnswer, error }) {
  const v = answers[q.id];

  if (q.type === 'boolean_branch') {
    return (
      <div className="sv-branch">
        {q.options.map((o) => (
          <button
            key={String(o.value)} type="button"
            className={`sv-branch-btn ${v === o.value ? 'is-active' : ''}`}
            aria-pressed={v === o.value}
            onClick={() => setAnswer(q.id, o.value)}
          >{o.label}</button>
        ))}
      </div>
    );
  }

  if (q.type === 'single_select') {
    return (
      <div className="sv-options">
        {q.options.map((o) => (
          <button
            key={o.value} type="button"
            className={`sv-chip ${v === o.value ? 'is-active' : ''}`}
            aria-pressed={v === o.value}
            onClick={() => setAnswer(q.id, o.value)}
          >{o.label}</button>
        ))}
        {q.allowOther && v === 'autre' && (
          <input
            className="sv-text" type="text" placeholder="Précise…"
            value={answers[q.otherField] || ''}
            onChange={(e) => setAnswer(q.otherField, e.target.value)}
          />
        )}
      </div>
    );
  }

  if (q.type === 'multi_select') {
    const selected = Array.isArray(v) ? v : [];
    const toggle = (val) => {
      const next = selected.includes(val)
        ? selected.filter((x) => x !== val)
        : [...selected, val];
      setAnswer(q.id, next);
    };
    const otherOn = answers[`__other_${q.id}`] === true;
    return (
      <div className="sv-options">
        {q.options.map((o) => (
          <button
            key={o.value} type="button"
            className={`sv-chip ${selected.includes(o.value) ? 'is-active' : ''}`}
            aria-pressed={selected.includes(o.value)}
            onClick={() => toggle(o.value)}
          >{o.label}</button>
        ))}
        {q.allowOther && (
          <button
            type="button"
            className={`sv-chip ${otherOn ? 'is-active' : ''}`}
            aria-pressed={otherOn}
            onClick={() => setAnswer(`__other_${q.id}`, !otherOn)}
          >Autre…</button>
        )}
        {q.allowOther && otherOn && (
          <input
            className="sv-text" type="text" placeholder="Précise…"
            value={answers[q.otherField] || ''}
            onChange={(e) => setAnswer(q.otherField, e.target.value)}
          />
        )}
      </div>
    );
  }

  if (q.type === 'scale_1_5') {
    return (
      <Scale
        q={q}
        value={Number.isInteger(v) ? v : null}
        na={naSelected(q, answers)}
        showNa={naAvailable(q, answers)}
        disabledNa={naForced(q, answers)}
        onPick={(n) => { setAnswer(`__na_${q.id}`, false); setAnswer(q.id, n); }}
        onNa={() => { const next = !(answers[`__na_${q.id}`]); setAnswer(`__na_${q.id}`, next); if (next) setAnswer(q.id, null); }}
      />
    );
  }

  if (q.type === 'short_text') {
    return (
      <textarea
        className="sv-textarea" rows={3} maxLength={q.maxLength || 500}
        placeholder={q.placeholder || ''}
        value={answers[q.id] || ''}
        onChange={(e) => setAnswer(q.id, e.target.value)}
      />
    );
  }

  return <div className="sv-error">Type de question non supporté : {q.type}</div>;
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function SurveyWizard({ form, accent }) {
  const [group] = useState(() => readParam('groupe', GROUP_PATTERN));
  // Création d'un code possible ? `null` tant que la phase du groupe n'est pas lue.
  const [canCreate, setCanCreate] = useState(group ? null : false);
  const [participantCode, setParticipantCode] = useState(loadParticipantCode);
  // Message à afficher sur l'écran de saisie quand on y revient (code retenu par
  // le navigateur mais inconnu du serveur, par exemple).
  const [codeNotice, setCodeNotice] = useState(null);
  const [schema, setSchema] = useState(null);
  const [session, setSession] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [{ section, answers }, setState] = useState({ section: 0, answers: {} });
  const codeConfirmed = CODE_PATTERN.test(participantCode);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  const phase = schema ? schema.phase : null;
  const cohort = session ? session.cohort : null;

  // Deux temps, et dans cet ordre : le serveur ne peut pas dire quelle vague
  // servir sans savoir de quel groupe de passation relève la répondante — les
  // groupes sont décalés dans le temps. On résout donc le calendrier à partir du
  // code, puis on demande le schéma de CE groupe. D'où la condition sur
  // `codeConfirmed` : sans code, il n'y a rien à charger.
  useEffect(() => {
    if (!codeConfirmed) return undefined;
    let alive = true;
    let resolved = null;
    resolveSurveySession(participantCode)
      .then((s) => {
        if (!alive) return null;
        if (!s.registered) {
          // Code du lien ou du navigateur inconnu du registre : rien à charger,
          // retour à la saisie plutôt qu'un faux « prochaine passation à venir ».
          rememberCode('');
          setCodeNotice(`Le code ${participantCode} n'est pas reconnu.`);
          setParticipantCode('');
          return null;
        }
        resolved = s;
        setSession(s);
        return fetchSurveySchema(form, s.cohort);
      })
      .then((s) => {
        if (!alive || !s) return;
        setSchema(s);
        // Le brouillon dépend de la vague : on ne le charge qu'une fois la phase connue.
        setState(loadState(form, resolved.cohort, s.phase));
      })
      .catch((e) => { if (alive) setLoadError(e.message); });
    return () => { alive = false; };
  }, [form, participantCode, codeConfirmed]);

  // Le lien de groupe est le même à chaque étape de l'étude : il ne doit proposer de
  // créer un code que pendant la phase amont de ce groupe, seule fenêtre où le serveur
  // l'accepte (`participants.self_register`). Avant, il s'ouvrait toujours sur « je
  // crée mon code », et une participante déjà inscrite qui y tapait le sien recevait
  // « déjà utilisé » ou « inscriptions closes ». Groupe inconnu ou lecture en échec :
  // pas de création (fail-closed), la saisie d'un code existant reste possible.
  useEffect(() => {
    if (!group) return undefined;
    let alive = true;
    fetchCohortPhase(group)
      .then((p) => { if (alive) setCanCreate(p.phase === 'amont'); })
      .catch(() => { if (alive) setCanCreate(false); });
    return () => { alive = false; };
  }, [group]);

  // Sauvegarde locale à chaque changement (reprise possible après fermeture).
  useEffect(() => {
    if (done || !phase) return;
    try {
      localStorage.setItem(draftKey(form, cohort, phase), JSON.stringify({ section, answers }));
    } catch (_) { /* ignore */ }
  }, [form, cohort, phase, section, answers, done]);

  const setAnswer = (key, value) =>
    setState((s) => ({ ...s, answers: { ...s.answers, [key]: value } }));

  const goto = (idx) => { setShowErrors(false); setState((s) => ({ ...s, section: idx })); window.scrollTo(0, 0); };

  const sections = schema ? schema.sections : [];
  // Un brouillon repris peut pointer au-delà du nombre de sections de la vague
  // (schéma modifié entre deux ouvertures) : on borne plutôt que de planter.
  const current = sections[Math.min(section, sections.length - 1)];
  const errors = useMemo(
    () => (current ? sectionErrors(current, answers) : {}),
    [current, answers]
  );
  const isLast = section === sections.length - 1;

  const next = () => {
    if (Object.keys(errors).length) { setShowErrors(true); return; }
    if (isLast) return handleSubmit();
    goto(section + 1);
  };

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitSurvey(form, buildPayload(schema, answers, participantCode));
      try { localStorage.removeItem(draftKey(form, cohort, phase)); } catch (_) { /* ignore */ }
      setDone(true);
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const style = accent ? { '--sv-primary': accent } : undefined;

  // Changer de code : téléphone prêté, ou code retenu par le navigateur qui n'est
  // pas le sien. Sans ce bouton, elle répondrait dans le dossier d'une autre.
  const changeCode = () => {
    rememberCode('');
    setCodeNotice(null);
    setSession(null);
    setSchema(null);
    setDone(false);
    setState({ section: 0, answers: {} });
    setParticipantCode('');
  };

  if (loadError) {
    return <div className="sv-root" style={style}><div className="sv-card sv-error">Impossible de charger le questionnaire : {loadError}</div></div>;
  }
  // Le code participant vient AVANT tout le reste : le serveur ne choisit la vague
  // qu'à partir du groupe auquel le registre rattache ce code. Un code inconnu
  // n'est rattaché à rien (`resolve_cohort`) : il faut donc le créer depuis le
  // lien de groupe, ou retaper celui qu'on a déjà.
  if (!codeConfirmed) {
    // Attendre la phase du groupe avant d'afficher la saisie : le mode par défaut en
    // dépend, et il ne doit pas basculer sous les doigts de la participante.
    if (canCreate === null) {
      return <div className="sv-root" style={style}><div className="sv-card">Chargement…</div></div>;
    }
    return (
      <CodeEntry
        group={group} canCreate={canCreate} notice={codeNotice} style={style}
        onReady={(code) => { rememberCode(code); setCodeNotice(null); setParticipantCode(code); }}
      />
    );
  }
  const whoami = (
    <p className="sv-whoami">
      Code : <strong>{participantCode}</strong> ·{' '}
      <button type="button" className="sv-linkbtn" onClick={changeCode}>ce n'est pas le mien</button>
    </p>
  );
  if (!schema) {
    return <div className="sv-root" style={style}><div className="sv-card">Chargement…</div></div>;
  }
  // Hors fenêtre de passation, le backend renvoie un schéma sans section : on
  // annonce la prochaine vague plutôt que d'afficher un formulaire vide (et de
  // récolter des soumissions parasites).
  if (!sections.length) {
    return (
      <div className="sv-root" style={style}>
        <div className="sv-card sv-thanks">
          <div className="sv-thanks-emoji">⏳</div>
          <h2>Prochaine passation à venir</h2>
          <p>
            Aucun questionnaire n'est ouvert pour le moment. Tu recevras un message
            dès que la prochaine étape de l'étude sera disponible.
          </p>
          {whoami}
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="sv-root" style={style}>
        <div className="sv-card sv-thanks">
          <div className="sv-thanks-emoji">🎉</div>
          <h2>Merci pour ta participation&nbsp;!</h2>
          <p>Ta réponse anonyme a bien été enregistrée. Elle contribue directement à l'étude CycleGala.</p>
          <p>
            Garde bien ton code <strong>{participantCode}</strong> : il te sera
            demandé à la prochaine étape.
          </p>
        </div>
      </div>
    );
  }

  const progress = Math.round(((section + 1) / sections.length) * 100);

  return (
    <div className="sv-root" style={style}>
      <div className="sv-header">
        <div className="sv-progress"><div className="sv-progress-fill" style={{ width: `${progress}%` }} /></div>
        <div className="sv-step">Étape {section + 1} sur {sections.length}</div>
        <h1 className="sv-title">{current.title}</h1>
        {section === 0 && schema.intro && <p className="sv-intro">{schema.intro}</p>}
        {section === 0 && whoami}
      </div>

      <form className="sv-form" onSubmit={(e) => { e.preventDefault(); next(); }}>
        {current.questions.filter((q) => isVisible(q, answers)).map((q) => (
          <div className={`sv-card sv-q ${showErrors && errors[q.id] ? 'has-error' : ''}`} key={q.id}>
            <label className="sv-label">
              {q.label}{q.required && <span className="sv-req" aria-hidden> *</span>}
            </label>
            <Question q={q} answers={answers} setAnswer={setAnswer} error={showErrors ? errors[q.id] : null} />
            {showErrors && (errors[q.id] || errors[q.otherField]) && (
              <div className="sv-field-error">{errors[q.id] || errors[q.otherField]}</div>
            )}
          </div>
        ))}

        {submitError && <div className="sv-card sv-error">Échec de l'envoi : {submitError}</div>}

        <div className="sv-actions">
          {section > 0 ? (
            <button type="button" className="sv-back" onClick={() => goto(section - 1)}>‹ Retour</button>
          ) : <span />}
          <button type="submit" className="sv-next" disabled={submitting}>
            {submitting ? 'Envoi…' : isLast ? 'Envoyer mes réponses' : 'Continuer ›'}
          </button>
        </div>
      </form>
    </div>
  );
}
